import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Flex, Image } from 'components/ui';
import Chiclet from 'components/Chiclet';
import { wrapFunctionalComponent } from 'utils/framework';
import { productSelector } from 'selectors/page/product/productSelector';
import productActions from 'actions/ProductActions';
import UI from 'utils/UI';
import localeUtils from 'utils/LanguageLocale';
import { SENTIMENT_POSI_ICON, SENTIMENT_NEGI_ICON } from 'ai/utils/imagePaths';
import { trackAspectChipClick } from 'ai/analytics/trackers/prsTrackers';

const NUMBER_OF_PROS_AND_CONS = 3;
const LIMIT_OF_REVIEWS = 6;

function AspectChips() {
    const product = useSelector(productSelector);
    const dispatch = useDispatch();
    const sentiments = product?.sentiments;
    const selectedSentiment = product?.selectedSentiment;

    if (!sentiments || !sentiments.length) {
        return null;
    }

    const pros = sentiments.filter(s => s.type === 'pros');
    const cons = sentiments.filter(s => s.type === 'cons');

    const handleChicletClick = (sentiment, count, type) => {
        const productId = product?.productDetails?.productId;

        if (selectedSentiment !== sentiment) {
            dispatch(productActions.setSelectedSentiment(sentiment));
            dispatch(
                productActions.loadHighlightedReviews({
                    sentiment,
                    type,
                    productId,
                    limit: LIMIT_OF_REVIEWS,
                    page: 0,
                    language: localeUtils.getCurrentLanguage(),
                    sentimentSource: 'seph_sentiment'
                })
            );
            trackAspectChipClick({ aspectName: sentiment, count, type, productId });
            UI.scrollTo({ elementId: 'ratings-reviews-container' });
        } else {
            dispatch(productActions.setSelectedSentiment(''));
            dispatch(productActions.removeHighlightedReviews());
        }
    };

    const renderChips = (items, type, iconSrc) => {
        return items.slice(0, NUMBER_OF_PROS_AND_CONS).map(item => (
            <Chiclet
                key={item.sentiment}
                variant='shadow'
                fontSize='sm'
                customPaddingX={2}
                isActive={item.sentiment === selectedSentiment}
                onClick={() => handleChicletClick(item.sentiment, item.count, type)}
            >
                <Flex
                    alignItems='center'
                    gap={1}
                >
                    <Image
                        src={iconSrc}
                        size={16}
                    />
                    <span>{`${item.sentiment} (${item.count.toLocaleString()})`}</span>
                </Flex>
            </Chiclet>
        ));
    };

    return (
        <Flex
            flexDirection='column'
            gap={3}
            data-at={Sephora.debug.dataAt('prs_aspect_chips')}
        >
            {pros.length > 0 && (
                <Flex
                    flexWrap='wrap'
                    gap={2}
                >
                    {renderChips(pros, 'pros', SENTIMENT_POSI_ICON)}
                </Flex>
            )}
            {cons.length > 0 && (
                <Flex
                    flexWrap='wrap'
                    gap={2}
                >
                    {renderChips(cons, 'cons', SENTIMENT_NEGI_ICON)}
                </Flex>
            )}
        </Flex>
    );
}

export default wrapFunctionalComponent(AspectChips, 'AspectChips');
